import type { ApiRequestOptions } from './core/ApiRequestOptions';
import { CancelablePromise } from './core/CancelablePromise';
import type { OpenAPIConfig } from './core/OpenAPI';
import { XHRHttpRequest } from './core/XHRHttpRequest';
import { GerasApi } from './GerasApi';

let isRefreshing = false;
let refreshQueue: Array<(token: string | null) => void> = [];

async function performRefresh(config: OpenAPIConfig): Promise<string | null> {
  const refreshToken = window.localStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  try {
    const client = new GerasApi({ BASE: config.BASE, TOKEN: refreshToken });
    const result = await client.auth.checkRefreshStrategy();
    window.localStorage.setItem('token', result.accessToken);
    window.localStorage.setItem('refreshToken', result.refreshToken);
    return result.accessToken;
  } catch {
    return null;
  }
}

export class AuthHttpRequest extends XHRHttpRequest {
  constructor(config: OpenAPIConfig) {
    super(config);
  }

  public override request<T>(options: ApiRequestOptions): CancelablePromise<T> {
    return new CancelablePromise<T>(async (resolve, reject, onCancel) => {
      const executeRequest = () => {
        const req = super.request<T>(options);
        onCancel(() => (req as any).cancel?.());
        return req;
      };

      try {
        resolve(await executeRequest());
      } catch (error: any) {
        if (error?.status !== 401) {
          reject(error);
          return;
        }

        if (isRefreshing) {
          const newToken = await new Promise<string | null>(res => refreshQueue.push(res));
          if (!newToken) { reject(error); return; }
          try { resolve(await executeRequest()); } catch (e) { reject(e); }
          return;
        }

        isRefreshing = true;
        const newToken = await performRefresh(this.config);
        isRefreshing = false;

        refreshQueue.forEach(cb => cb(newToken));
        refreshQueue = [];

        if (!newToken) {
          window.localStorage.removeItem('token');
          window.localStorage.removeItem('refreshToken');
          window.location.replace('/login');
          reject(error);
          return;
        }

        try { resolve(await executeRequest()); } catch (e) { reject(e); }
      }
    });
  }
}
